/**
 * This is basically an updated version of https://github.com/jaystack/odata-v4-server/blob/master/src/lib/visitor.ts
 * In order to handle custom date formats, I needed to make some significant changes
 */
/* eslint-disable */
import { Token, TokenType } from 'odata-v4-parser/lib/lexer';
import { DateParser } from './DateParser';

export interface VisitorFuncRes {
  (a: any): any
}

export interface VisitorFunc {
  (node: Token, context: any): VisitorFuncRes | Array<VisitorFuncRes>;
}

export interface VisitorMap {
  [key: string]: VisitorFunc;
}

const minDateTime = new Date(-8640000000000000);
const maxDateTime = new Date(8640000000000000);

const defaultDateParserFn = (val: any) => {
  let parsedDate: Date = new Date('invalid');

  if (val) {
    const parts: number[] = val.split('-').map((part: string) => parseInt(part, 10));

    if (parts.length === 3) {
      return new Date(parts[0], parts[1] + 1, parts[2]);
    }
  }

  return parsedDate;
};

const getAsDate = (val: any, parser: any) => {
  if (typeof val === 'string') {
    // attempt to parse the date
    if (parser) {
      let parsed = parser.parseDate(val);
      // console.log(parsed);

      if (isNaN(parsed.getTime())) {
        // try parsing using yyyy-mm-dd too
        parsed = defaultDateParserFn(val);
      }

      return parsed;
    }
    else {
      return new Date(val);
    }
  }
  else {
    return val;
  }
};

export const ODataMethodMap: any = {
  round: (v: any) => Math.round(v),
  indexof: (v: any, i: any) => v.indexOf && v.indexOf(i),
  substring: (v: any, i: any) => typeof v === 'undefined' || v === null ? '' : v.toString().substr(i - 1),
  contains: (v: any, i: any) => typeof v === 'undefined' || v === null ? false : v.toString().includes(i),
  endswith: (v: any, i: any) => typeof v === 'undefined' || v === null ? false : v.toString().endsWith(i),
  startswith: (v: any, i: any) => typeof v === 'undefined' || v === null ? false : v.toString().startsWith(i),
  length: (v: any) => typeof v === 'undefined' || v === null ? 0 : v.toString().length,
  tolower: (v: any) => typeof v === 'undefined' || v === null ? '' : v.toLowerCase(),
  toupper: (v: any) => typeof v === 'undefined' || v === null ? '' : v.toUpperCase(),
  trim: (v: any) => typeof v === 'undefined' || v === null ? '' : v.trim(),
  concat: (v: any, i: any) => typeof v === 'undefined' || v === null ? i : v.toString().concat(i),
  year: (v: any, parser: any) => getAsDate(v, parser).getFullYear(),
  month: (v: any, parser: any) => getAsDate(v, parser).getMonth() + 1,
  day: (v: any, parser: any) => getAsDate(v, parser).getDate(),
  hour: (v: any, parser: any) => getAsDate(v, parser).getHours(),
  minute: (v: any, parser: any) => getAsDate(v, parser).getMinutes(),
  second: (v: any, parser: any) => getAsDate(v, parser).getSeconds(),
  now: () => new Date(),
  maxdatetime: () => maxDateTime,
  mindatetime: () => minDateTime,
  floor: (v: any) => Math.floor(v),
  ceiling: (v: any) => Math.ceil(v)
  // isof: (v, i) => return v
}

export class FilterVisitor implements VisitorMap {
  [k: string]: VisitorFunc | any;

  constructor(public dateParser: DateParser) { }

  Visit(node: Token, context: any): any {
    // console.log("Visiting: ", node.type)
    if (!node) {
      throw new Error('Node cannot be empty')
    }

    switch (node.type) {
      // these are auto handled by visitor bubbling
      case TokenType.CollectionPathExpression:
      case TokenType.LambdaPredicateExpression:
      case TokenType.MemberExpression:
      case TokenType.SingleNavigationExpression:
      case TokenType.CommonExpression:
      case TokenType.ArrayOrObject:
      case undefined:
        break;

      default:
        const fun = this[`Visit${node.type}`];

        if (fun) {
          return fun.call(this, node, context);
        }

        console.log(`Unhandled node type, falling back: ${node.type}`);
    }

    return this.Visit(node.value, context)
  }

  // todo fix AST so that we dont need this
  public VisitFirstMemberExpression(node: Token, context: any) {
    if (Array.isArray(node.value)) {
      const [current, next] = node.value;

      return this.VisitODataIdentifier(<Token>{ value: { current, next } }, context)
    }

    return this.Visit(node.value, context);
  }

  private VisitBinaryExpression(node: Token, context: any) {
    return [this.Visit(node.value.left, context), this.Visit(node.value.right, context)];
  }

  protected VisitBoolParenExpression(node: Token, context: any) {
    const inner = this.Visit(node.value, context);
    return (a: any) => !!inner(a);
  }

  protected VisitLambdaVariableExpression(_node: Token, _context: any) {
    return (a: any) => a;
  }

  protected VisitCountExpression(_node: Token, _context: any) {
    return (a: any) => (a && a.length) || 0;
  }

  protected VisitAllExpression(node: Token, context: any) {
    const predicate = this.Visit(node.value.predicate, context);
    return (a: any) => a.every && a.every(predicate);
  }

  protected VisitAnyExpression(node: Token, context: any) {
    const predicate = this.Visit(node.value.predicate, context);
    return (a: any) => a.some && a.some(predicate);
  }

  protected VisitFilter(node: Token, context: any) {
    const predicate = this.Visit(node.value, context);
    return (a: any) => !!predicate(a);
  }

  protected VisitNotExpression(node: Token, context: any) {
    const expression = this.Visit(node.value, context);
    return (a: any) => !expression(a);
  }

  protected VisitEqualsExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) === right(a);
  }

  protected VisitNotEqualsExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) !== right(a);
  }

  protected VisitGreaterThanExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) > right(a);
  }

  protected VisitLesserThanExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) < right(a);
  }

  protected VisitLesserOrEqualsExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) <= right(a);
  }

  protected VisitGreaterOrEqualsExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) >= right(a);
  }

  protected VisitImplicitVariableExpression(_node: Token, _context: any) {
    return (a: any) => a;
  }

  protected VisitAndExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) && right(a);
  }

  protected VisitAddExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) + right(a);
  }

  protected VisitSubExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) - right(a);
  }

  protected VisitMulExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) * right(a);
  }

  protected VisitDivExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) / right(a);
  }

  protected VisitModExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) % right(a);
  }

  protected VisitParenExpression(node: Token, context: any) {
    const fn = this.Visit(node.value, context);
    return (a: any) => fn(a);
  }

  protected getLiteral(node: Token): any {
    switch (node.value) {
      case 'null':
        return null;

      case 'Edm.Int16':
      case 'Edm.Int32':
      case 'Edm.Int64':
      case 'Edm.SByte':
        return parseInt(node.raw, 10);

      case 'Edm.Decimal':
        return parseFloat(node.raw);

      case 'Edm.Double':
        switch (node.raw) {
          case 'INF':
            return Infinity;

          default:
            return parseFloat(node.raw);
        }

      case 'Edm.Boolean':
        return node.raw === 'true';

      // todo: check if string is actually a valid literal type
      case 'string':
      case 'Edm.String':
        return decodeURIComponent(node.raw).replace(/'/g, '').replace(/"/g, '');

      case 'Edm.DateTimeOffset':
      case 'Edm.Date':
        return this.dateParser ? this.dateParser.parseDate(node.raw) : new Date(node.raw);

      case 'Edm.TimeOfDay':
        return new Date(`1970-01-01T${node.raw}Z`);

      case 'Edm.Duration': {
        const m = node.raw.match(/P([0-9]*D)?T?([0-9]{1,2}H)?([0-9]{1,2}M)?([\.0-9]*S)?/); // eslint-disable no-useless-escape

        if (m != null) {
          const d = new Date(0);

          for (let i = 1; i < m.length; i++) {
            switch (m[i].slice(-1)) {
              case 'D':
                d.setDate(parseInt(m[i]));
                continue;

              case 'H':
                d.setHours(parseInt(m[i]));
                continue;

              case 'M':
                d.setMinutes(parseInt(m[i]));
                continue;

              case 'S':
                d.setSeconds(parseInt(m[i]));
                continue;
            }
          }

          return d;
        }
      }

      // fallthrough
      default:
        console.log('unknown value type:' + node.value, node.raw); // eslint-disable no-fallthrough
    }

    return node.raw;
  }

  protected VisitLiteral(node: Token, _context: any) {
    return (_a: any) => this.getLiteral(node);
  }

  protected VisitMethodCallExpression(node: Token, context: any) {
    const method = ODataMethodMap[node.value.method];
    const params = (node.value.parameters || []).map((p: any) => this.Visit(p, context));

    if (method == null) {
      console.log(`Unknown method ${node.value.method}`);
      return (a: any) => a;
    }

    return (a: any) => {
      const paramValues = params.map((p: any) => p(a));

      if (this.dateParser) {
        paramValues.push(this.dateParser);
      }

      return method.apply(this, paramValues);
    };
  }

  protected VisitPropertyPathExpression(node: Token, context: any) {
    if (node.value.name) {
      return (a: any) => a[node.value.name];
    }

    if (node.value.current && node.value.next) {
      const current = this.Visit(node.value.current, context);
      const next = this.Visit(node.value.next, context);

      return (a: any) => next(current(a) || {});
    }

    return this.Visit(node.value, context);
  }

  protected VisitODataIdentifier(node: Token, context: any) {
    if (node.value.name) {
      return (a: any) => a[node.value.name];
    }

    const current = this.Visit(node.value.current, context);
    const next = this.Visit(node.value.next, context);

    return (a: any) => next(current(a) || {});
  }

  protected VisitIsOfExpression(node: Token, context: any) {
    const type = this.Visit(node.value.typename, context);

    if (!node.value.target) {
      return (a: any) => {
        return a.constructor.name === type(a);
      };
    }

    const target = this.Visit(node.value.target, context);

    return (a: any) => target(a).constructor.name === type(a);
  }

  protected VisitQualifiedEntityTypeName(node: Token, context: any) {
    return (a: any) => this.Visit(node.value, context)(a);
  }

  protected VisitEntityTypeName(node: Token, _context: any) {
    return (_a: any) => node.value.name;
  }

  protected VisitOrExpression(node: Token, context: any) {
    const [left, right] = this.VisitBinaryExpression(node, context);
    return (a: any) => left(a) || right(a);
  }

  private resolveIdentifier(node: any) {
    switch (node.value) {
      case 'EntityTypeName':
        return node.raw.split('.').slice(-1)[0];
    }
  }

  // this needs double check. why is model type model as 'identifier'
  protected VisitIdentifier(node: any, _context: any) {
    return (_a: any) => this.resolveIdentifier(node);
  }

  protected VisitArray(node: Token, context: any) {
    const items = (node.value.items || []).map((item: any) => this.Visit(item, context));
    return (a: any) => [...items.map((item: any) => item(a))];
  }

  protected VisitNegateExpression(node: Token, context: any) {
    const exp = this.Visit(node.value, context);
    return (a: any) => -exp(a);
  }
}
